import React, { useState } from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

// Shared with SettingContact.js, which runs this at save-time so an
// invalid number can't slide through just because the field was never
// blurred (or its inline warning was ignored) - the backend's own
// phone/whatsapp regex is looser than this (it allows a bare digit string
// with no country code), so this is the one place that actually enforces
// "must be a plausible international number with a country code" before the
// save request is even sent. Empty is treated as valid - these fields are
// optional, this only judges whatever was actually typed.
export function isValidPhoneValue(value) {
  const trimmed = (value || '').trim();
  if (!trimmed) return true;
  const parsed = parsePhoneNumberFromString(trimmed);
  return !!parsed && (parsed.isValid() || parsed.isPossible());
}

// Plain, boring, fully-controlled text input - deliberately NOT a
// react-phone-input-2-style widget, which reformats the displayed value on
// every keystroke and resets the cursor to the end when editing a
// controlled component (a known, unresolved issue in that library). This
// component never rewrites what the user sees; it only filters keystrokes
// and validates on blur, so editing in place always works normally.
const ValidatedPhoneInput = ({ value, onChange, flush = false }) => {
  const [touched, setTouched] = useState(false);

  const trimmed = (value || '').trim();
  let errorMessage = '';
  if (touched && trimmed) {
    const parsed = parsePhoneNumberFromString(trimmed);
    if (!parsed) {
      errorMessage = 'Invalid phone number format - include the country code, e.g. +6771234567';
    } else if (!(parsed.isValid() || parsed.isPossible())) {
      errorMessage = 'Invalid number - check the country code and digit count';
    }
  }

  const handleChange = (e) => {
    // Letters can never appear in the field at all - only digits and a
    // single leading "+" survive, regardless of what was typed or pasted.
    const sanitized = e.target.value
      .replace(/[^\d+]/g, '')
      .replace(/(?!^)\+/g, '');
    onChange(sanitized);
  };

  const baseClasses = flush
    ? 'block w-full rounded-[18px] border bg-white px-4 py-3 text-[14px] leading-6 text-[#24324d] shadow-none outline-none transition-all duration-200 placeholder:text-[#99a4bc] hover:bg-[#fbfcff] focus:bg-white focus:ring-4 sm:text-sm'
    : 'block w-full rounded-[14px] border bg-white px-4 py-3 text-[14px] leading-6 text-[#24324d] shadow-none outline-none transition-all duration-200 placeholder:text-[#99a4bc] hover:bg-[#fbfcff] focus:bg-white focus:ring-4 sm:text-sm';
  const borderClasses = errorMessage
    ? 'border-[#f3b4b4] hover:border-[#eb9c9c] focus:border-[#e37f7f] focus:ring-[rgba(220,38,38,0.08)]'
    : 'border-[#d7deef] hover:border-[#c8d3fb] focus:border-[#b7c5ef] focus:ring-[rgba(74,92,255,0.08)]';

  return (
    <div className={flush ? 'mt-0' : 'mt-2'}>
      <input
        type="text"
        inputMode="tel"
        value={value || ''}
        onChange={handleChange}
        onBlur={() => setTouched(true)}
        placeholder="e.g. +6771234567 (include country code)"
        className={`${baseClasses} ${borderClasses}`}
      />
      {errorMessage && (
        <p className="mt-1 text-[12px] text-[#c0392b]">{errorMessage}</p>
      )}
    </div>
  );
};

export default ValidatedPhoneInput;


